import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { PageHeader, EmptyState, TableSkeleton } from "@/components/common/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollText, Download, Search } from "lucide-react";

const MODULES = ["auth", "users", "branches", "roles", "settings", "notifications"];

export default function AuditLogsPage() {
  const { t, can } = useApp();
  const [logs, setLogs] = useState(null);
  const [module, setModule] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLogs(null);
    const params = new URLSearchParams();
    if (module !== "all") params.set("module", module);
    if (status !== "all") params.set("status", status);
    if (search) params.set("search", search);
    try { const { data } = await api.get(`/audit-logs?${params}`); setLogs(data); } catch { setLogs([]); }
  }, [module, status, search]);
  useEffect(() => { const tm = setTimeout(load, 300); return () => clearTimeout(tm); }, [load]);

  const exportCsv = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/audit-logs/export`, "_blank");

  return (
    <div>
      <PageHeader title={t("auditLogs")} subtitle="Security and administrative activity trail">
        {can("reports.export") && <Button variant="outline" size="sm" className="gap-2" onClick={exportCsv} data-testid="audit-export-btn"><Download className="h-4 w-4" />{t("export")}</Button>}
      </PageHeader>

      <div className="bg-card border border-border rounded-md">
        <div className="p-4 border-b border-border flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="audit-search" />
          </div>
          <Select value={module} onValueChange={setModule}>
            <SelectTrigger className="w-40" data-testid="audit-module-filter"><SelectValue placeholder="Module" /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("all")} Modules</SelectItem>{MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="audit-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("all")} {t("status")}</SelectItem><SelectItem value="success">success</SelectItem><SelectItem value="failed">failed</SelectItem><SelectItem value="blocked">blocked</SelectItem></SelectContent>
          </Select>
        </div>
        {logs === null ? <TableSkeleton cols={6} /> : logs.length === 0 ? <EmptyState icon={ScrollText} title={t("noAuditRecords")} /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Timestamp</TableHead><TableHead>User</TableHead><TableHead>Module</TableHead>
                <TableHead>Action</TableHead><TableHead>{t("description")}</TableHead><TableHead>IP</TableHead><TableHead>{t("status")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-sm"><p className="font-medium">{l.user_name}</p><p className="text-xs text-muted-foreground">{l.role}</p></TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{l.module}</Badge></TableCell>
                    <TableCell className="text-xs font-mono">{l.action}</TableCell>
                    <TableCell className="text-sm max-w-xs truncate">{l.description}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{l.ip_address || "—"}</TableCell>
                    <TableCell><Badge variant={l.status === "success" ? "secondary" : "destructive"} className="text-[10px]">{l.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
